import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import authService from '../../services/keycloak/authService';
import AccountTypeSelection from './AccountTypeSelection';
import PhoneInput from 'react-phone-input-2';
import countriesData from '../../data/countriesData.json';
import Toast from '../../ui/Toast';
import 'react-phone-input-2/lib/style.css';
import './Signup.css';

function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const turnstileRef = useRef(null);
  const [step, setStep] = useState('selection'); // 'selection' or 'form'
  const [accountType, setAccountType] = useState(null); // 'personal' or 'organization'
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    // Organization-specific fields
    companyName: '',
    companyRegistrationNumber: '',
    industry: '',
    country: '',
    phoneNumber: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [requiresCaptcha, setRequiresCaptcha] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState(5);
  const [expirationTime, setExpirationTime] = useState('5 minutes');
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });

  // Invitation logic
  const [inviteToken, setInviteToken] = useState(null);
  const [inviteDetails, setInviteDetails] = useState(null);
  const isPreConfigured = inviteToken && inviteDetails?.role === 'organization_admin';

  // Parse invite token from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('invite');
    if (token) {
      setInviteToken(token);
      validateInviteToken(token);
    }
  }, [location]);

  const validateInviteToken = async (token) => {
    try {
      // In Docker, the frontend proxy is /api
      const response = await fetch(`/api/workspace/invite/${token}`);
      const data = await response.json();

      if (response.ok && data.success) {
        setInviteDetails(data.invite);
        const { invite } = data;

        // Map invite details to form data
        setFormData((prev) => ({
          ...prev,
          email: invite.email,
          firstName: invite.settings?.owner_first_name || prev.firstName,
          lastName: invite.settings?.owner_last_name || prev.lastName,
          companyName: invite.workspaceName || prev.companyName,
          companyRegistrationNumber:
            invite.settings?.company_registration_number || prev.companyRegistrationNumber,
          industry: invite.industry || prev.industry,
          country: invite.settings?.country || prev.country,
          phoneNumber: invite.settings?.phone_number || prev.phoneNumber,
        }));

        if (invite.role === 'organization_admin') {
          setAccountType('organization');
        } else {
          setAccountType('personal');
        }
        setStep('form');
      } else {
        setErrors({ submit: data.message || 'Invalid or expired invitation link' });
      }
    } catch (err) {
      console.error('Failed to validate invite token:', err);
      const msg = 'Failed to validate invitation link';
      setErrors({ submit: msg });
      showError(msg);
    }
  };

  // Check signup requirements on component mount
  useEffect(() => {
    checkSignupRequirements();
    fetchExpirationTime();
  }, []);

  const fetchExpirationTime = async () => {
    try {
      const response = await fetch('/api/auth/email-token-lifespan');
      const data = await response.json();
      if (data.success && data.data.displayText) {
        setExpirationTime(data.data.displayText);
      }
    } catch (err) {
      console.error('Failed to fetch expiration time:', err);
      // Keep default value if fetch fails
    }
  };

  const checkSignupRequirements = async () => {
    try {
      const response = await fetch('/api/auth/signup-requirements');
      const data = await response.json();
      if (data.success) {
        setRequiresCaptcha(data.requiresCaptcha);
        setAttemptsRemaining(data.attemptsRemaining || 0);
      }
    } catch (err) {
      console.error('Failed to check signup requirements:', err);
    }
  };

  const handleCaptchaChange = (token) => {
    setTurnstileToken(token);
    if (errors.captcha) {
      setErrors((prev) => ({ ...prev, captcha: '' }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'All fields are required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'All fields are required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'All fields are required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'All fields are required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else {
      // Check individual password requirements
      const hasLowercase = /(?=.*[a-z])/.test(formData.password);
      const hasUppercase = /(?=.*[A-Z])/.test(formData.password);
      const hasNumber = /(?=.*\d)/.test(formData.password);
      const hasSpecialChar = /(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/.test(formData.password);

      const missingRequirements = [];
      if (!hasUppercase) missingRequirements.push('uppercase');
      if (!hasLowercase) missingRequirements.push('lowercase');
      if (!hasNumber) missingRequirements.push('number');
      if (!hasSpecialChar) missingRequirements.push('special character');

      if (missingRequirements.length > 0) {
        const formattedRequirements = missingRequirements
          .map((req, index) => {
            if (index === missingRequirements.length - 1 && missingRequirements.length > 1) {
              return 'and ' + req;
            }
            return req;
          })
          .join(', ')
          .replace(/, and/, ' and');
        newErrors.password = `Password must contain ${formattedRequirements}`;
      }
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    // Organization-specific validation
    if (accountType === 'organization') {
      if (!formData.companyName.trim()) {
        newErrors.companyName = 'Company name is required';
      }
      if (!formData.companyRegistrationNumber.trim()) {
        newErrors.companyRegistrationNumber = 'Company registration number is required';
      }
      if (!formData.country.trim()) {
        newErrors.country = 'Country is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getFirstErrorMessage = () => {
    const errorFields = [
      'firstName',
      'lastName',
      'email',
      'password',
      'confirmPassword',
      'companyName',
      'companyRegistrationNumber',
      'country',
      'agreeToTerms',
      'captcha',
    ];
    for (const field of errorFields) {
      if (errors[field]) {
        return errors[field];
      }
    }
    return errors.submit || null;
  };

  const showError = (message) => {
    setToast({ show: true, message, type: 'error' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showError(getFirstErrorMessage());
      return;
    }

    // Check if CAPTCHA is required but not completed
    if (requiresCaptcha && !turnstileToken) {
      console.warn('[Signup] CAPTCHA required but no token present');
      setErrors({
        captcha: 'Please complete the CAPTCHA verification',
      });
      showError('Please complete the CAPTCHA verification');
      return;
    }

    setIsLoading(true);

    try {
      // Create user through backend API via nginx proxy
      const payload = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        password: formData.password,
        accountType: accountType, // 'personal' or 'organization'
      };

      // Add organization-specific fields if applicable
      if (accountType === 'organization') {
        payload.companyName = formData.companyName;
        payload.companyRegistrationNumber = formData.companyRegistrationNumber;
        payload.industry = formData.industry;
        payload.country = formData.country;
        payload.phoneNumber = formData.phoneNumber;
      }

      // Include CAPTCHA token if available (send as both field names for compatibility)
      if (turnstileToken) {
        payload.turnstileToken = turnstileToken;
        payload.captchaToken = turnstileToken; // Backend also checks this field
      }

      // Include invite token if present
      if (inviteToken) {
        payload.inviteToken = inviteToken;
      }

      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();

        // IMPORTANT: Recheck requirements from server after failed attempt
        await checkSignupRequirements();

        // Check if CAPTCHA is now required from response
        if (error.requiresCaptcha) {
          setRequiresCaptcha(true);
          if (turnstileRef.current) {
            turnstileRef.current.reset();
          }
          setTurnstileToken(null);
        }

        throw new Error(error.message || 'Registration failed');
      }

      if (inviteToken) {
        navigate('/login');
      } else {
        // Show success message UI
        setSuccessMessage(true);
      }
      setIsLoading(false);

      // Reset CAPTCHA on success
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setTurnstileToken(null);
    } catch (err) {
      setErrors({
        submit: err.message || 'Failed to create account. Please try again.',
      });
      showError(err.message || 'Failed to create account. Please try again.');
      console.error('Signup error:', err);
      setIsLoading(false);

      // Recheck requirements after error to update UI
      await checkSignupRequirements();
    }
  };

  const handleOAuthSignup = async (provider) => {
    setIsLoading(true);
    try {
      // Redirect to Keycloak login with Google identity provider hint
      const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8081';
      const realm = process.env.REACT_APP_KEYCLOAK_REALM || 'ai-leadgen';
      const clientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'ai-leadgen-frontend';
      const redirectUri = window.location.origin;

      const loginUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid&kc_idp_hint=${provider}`;

      window.location.href = loginUrl;
    } catch (err) {
      setErrors({
        submit: `Failed to sign up with ${provider}. Please try again.`,
      });
      showError(`Failed to sign up with ${provider}. Please try again.`);
      console.error(`${provider} signup error:`, err);
      setIsLoading(false);
    }
  };

  const handleSelectAccountType = (type) => {
    setAccountType(type);
    setStep('form');
  };

  const handleBackToSelection = () => {
    setStep('selection');
    setAccountType(null);
    setErrors({});
  };

  // Show account type selection screen
  if (step === 'selection') {
    return (
      <div className="auth-container">
        <AccountTypeSelection
          onSelectType={handleSelectAccountType}
          onBack={() => navigate('/login')}
        />

        <div className="auth-background">
          <div className="auth-decoration decoration-1"></div>
          <div className="auth-decoration decoration-2"></div>
        </div>
      </div>
    );
  }

  // If registration is successful, show success message
  if (successMessage) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Check Your Email</h1>
            <p>Account created successfully</p>
          </div>

          <div className="success-message">
            <div className="success-icon">✓</div>
            <p>
              We've sent a verification email to <strong>{formData.email}</strong>
            </p>
            <p>
              Please check your email and click the verification link to activate your account. The
              link will expire in {expirationTime}.
            </p>
          </div>

          <div className="auth-footer">
            <p>
              Didn't receive the email?{' '}
              <button onClick={() => setSuccessMessage(false)} className="auth-link-button">
                Try again
              </button>
            </p>
            <p>
              <Link to="/login" className="auth-link">
                Back to sign in
              </Link>
            </p>
          </div>
        </div>

        <div className="auth-background">
          <div className="auth-decoration decoration-1"></div>
          <div className="auth-decoration decoration-2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container login-split-layout">
      {/* Left Panel - Welcome Section */}
      <div className="auth-left-panel">
        <div className="welcome-content">
          <div className="left-logo-container">
            <img src="/image/Logo.png" alt="AI LeadGen Logo" className="left-auth-logo" />
          </div>

          <div className="welcome-message">
            <h2 className="welcome-subtitle">Start your journey with us</h2>
            <h1 className="welcome-title">JOIN US TODAY</h1>
          </div>

          <p className="welcome-description">
            Create your account and unlock powerful lead generation tools. Streamline your sales
            process with intelligent market analysis and comprehensive prospect insights.
          </p>
        </div>

        <div className="decorative-elements">
          <div className="decoration-circle circle-1"></div>
          <div className="decoration-circle circle-2"></div>
          <div className="decoration-circle circle-3"></div>
          <div className="decoration-grid"></div>
        </div>
      </div>

      {/* Right Panel - Signup Form */}
      <div className="auth-right-panel" data-accounttype={accountType}>
        {!inviteToken && (
          <button className="back-button-circle" onClick={handleBackToSelection} type="button">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        {toast.show && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ ...toast, show: false })}
          />
        )}

        <div className="auth-form-container">
          <div className="auth-form-header">
            <h2>
              {inviteToken
                ? inviteDetails?.role === 'organization_admin'
                  ? 'JOIN US TODAY'
                  : 'Join Workspace'
                : accountType === 'personal'
                  ? 'Create a Personal Account'
                  : 'Create an Organization Account'}
            </h2>
            {inviteToken && inviteDetails?.role !== 'organization_admin' && (
              <p>{`You have been invited to join ${inviteDetails?.workspaceName || 'a workspace'}.`}</p>
            )}
          </div>
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">
                  First Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Enter your first name"
                  disabled={isLoading || isPreConfigured}
                  autoComplete="given-name"
                  className={errors.firstName ? 'error' : ''}
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">
                  Last Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Enter your last name"
                  disabled={isLoading || isPreConfigured}
                  autoComplete="family-name"
                  className={errors.lastName ? 'error' : ''}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">
                {accountType === 'organization' ? 'Work Email Address' : 'Email Address'}{' '}
                <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="name@email.com"
                value={formData.email}
                onChange={handleChange}
                className={`auth-input ${errors.email ? 'input-error' : ''}`}
                disabled={!!inviteToken || isLoading}
                autoComplete="email"
              />
            </div>

            {/* Organization-specific fields */}
            {accountType === 'organization' && (
              <>
                <div className="form-group">
                  <label htmlFor="companyName">
                    Company Name <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Enter your company name"
                    disabled={isLoading || isPreConfigured}
                    autoComplete="organization"
                    className={errors.companyName ? 'error' : ''}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="companyRegistrationNumber">
                    Company Registration Number <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="companyRegistrationNumber"
                    name="companyRegistrationNumber"
                    value={formData.companyRegistrationNumber}
                    onChange={handleChange}
                    placeholder="Enter registration number"
                    disabled={isLoading || isPreConfigured}
                    className={errors.companyRegistrationNumber ? 'error' : ''}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="industry">Industry</label>
                    <input
                      type="text"
                      id="industry"
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      placeholder="e.g., Technology, Finance"
                      disabled={isLoading || isPreConfigured}
                      className={errors.industry ? 'error' : ''}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="country">
                      Country <span style={{ color: 'red' }}>*</span>
                    </label>
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      disabled={isLoading || isPreConfigured}
                      className={`auth-input ${errors.country ? 'error' : ''}`}
                    >
                      <option value="">Select your country</option>
                      {countriesData.map((c) => (
                        <option key={c.code} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="phoneNumber">Phone Number</label>
                  <PhoneInput
                    country={
                      countriesData.find((c) => c.name === formData.country)?.code?.toLowerCase() ||
                      'my'
                    }
                    value={formData.phoneNumber}
                    onChange={(phone) => {
                      setFormData((prev) => ({
                        ...prev,
                        phoneNumber: phone,
                      }));
                      if (errors.phoneNumber) {
                        setErrors((prev) => ({ ...prev, phoneNumber: '' }));
                      }
                    }}
                    inputProps={{
                      name: 'phoneNumber',
                      id: 'phoneNumber',
                      disabled: isLoading || isPreConfigured,
                      className: errors.phoneNumber ? 'error' : '',
                    }}
                    disableDropdown={true}
                  />
                </div>
              </>
            )}

            <div className="form-group">
              <label htmlFor="password">
                Password <span style={{ color: 'red' }}>*</span>
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a strong password"
                  disabled={isLoading}
                  autoComplete="new-password"
                  className={errors.password ? 'error' : ''}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 12C2 12 5 5 12 5C19 5 22 12 22 12C22 12 19 19 12 19C5 19 2 12 2 12Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 3L21 21M10.584 10.587C10.2087 10.9624 9.99778 11.4708 9.99756 12.0013C9.99734 12.5317 10.2078 13.0403 10.5829 13.416C10.958 13.7916 11.4664 14.0025 11.9968 14.0027C12.5273 14.003 13.0359 13.7925 13.4115 13.4174M17.357 17.349C15.726 18.449 13.942 19 12 19C7 19 3.5 15 2 12C2.80712 10.1298 4.0416 8.47059 5.603 7.137M9.363 5.365C10.2204 5.11972 11.1082 4.99684 12 5C17 5 20.5 9 22 12C21.526 13.0452 20.9274 14.0279 20.218 14.927"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">
                Confirm Password <span style={{ color: 'red' }}>*</span>
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  disabled={isLoading}
                  autoComplete="new-password"
                  className={errors.confirmPassword ? 'error' : ''}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isLoading}
                >
                  {showConfirmPassword ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 12C2 12 5 5 12 5C19 5 22 12 22 12C22 12 19 19 12 19C5 19 2 12 2 12Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 3L21 21M10.584 10.587C10.2087 10.9624 9.99778 11.4708 9.99756 12.0013C9.99734 12.5317 10.2078 13.0403 10.5829 13.416C10.958 13.7916 11.4664 14.0025 11.9968 14.0027C12.5273 14.003 13.0359 13.7925 13.4115 13.4174M17.357 17.349C15.726 18.449 13.942 19 12 19C7 19 3.5 15 2 12C2.80712 10.1298 4.0416 8.47059 5.603 7.137M9.363 5.365C10.2204 5.11972 11.1082 4.99684 12 5C17 5 20.5 9 22 12C21.526 13.0452 20.9274 14.0279 20.218 14.927"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <label className="terms-checkbox">
              <input
                type="checkbox"
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleChange}
                disabled={isLoading}
                className={errors.agreeToTerms ? 'error' : ''}
              />
              <span>
                I agree to the{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer">
                  Terms & Conditions
                </Link>{' '}
                and{' '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </Link>
              </span>
            </label>

            {/* Cloudflare Turnstile Widget */}
            {requiresCaptcha && (
              <div className="turnstile-container">
                <Turnstile
                  key={`turnstile-${requiresCaptcha}-${accountType || 'default'}`}
                  ref={turnstileRef}
                  siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAACVqQ7XRVEPJLGVX'}
                  options={{
                    theme: 'light',
                    appearance: 'always',
                    size: 'normal',
                  }}
                  onSuccess={handleCaptchaChange}
                  onError={(error) => {
                    console.error('[Turnstile] Error:', error);
                    setErrors((prev) => ({
                      ...prev,
                      captcha: 'CAPTCHA verification failed. Please try again.',
                    }));
                    setTurnstileToken(null);
                  }}
                  onExpire={() => {
                    console.warn('[Turnstile] Token expired');
                    setTurnstileToken(null);
                  }}
                />
              </div>
            )}

            {/* Attempts Warning */}
            {attemptsRemaining <= 2 && attemptsRemaining > 0 && !requiresCaptcha && (
              <div
                style={{
                  background: '#fff3cd',
                  padding: '12px',
                  borderRadius: '6px',
                  marginTop: '15px',
                  fontSize: '14px',
                  color: '#856404',
                  textAlign: 'center',
                }}
              >
                ⚠️ {attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} remaining before
                CAPTCHA is required
              </div>
            )}

            <button
              type="submit"
              className="auth-button primary"
              disabled={isLoading || (requiresCaptcha && !turnstileToken)}
            >
              {isLoading ? 'Creating Account...' : 'SIGN UP'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Signup;
